import React from "react";
import { Box, Typography, Grid2, Divider } from "@mui/material";
import { ArrowUp, ArrowDown, ArrowNoChange } from "../Icons/ArrowIcon";
import { PrimaryTooltip } from "../StyledComponent";

/**
 * KPICard - A reusable KPI card component for displaying metrics
 *
 * @param {Object} props
 * @param {string} props.title - Card title
 * @param {string} props.titleTooltip - Tooltip text for the title
 * @param {string} props.value - Main value to display
 * @param {string} props.unit - Optional unit to display next to the value
 * @param {string} props.previousValue - Previous value for trend comparison
 * @param {string} props.trend - Trend direction: "up", "down", or "neutral"
 * @param {boolean} props.isPositive - Whether the trend is positive (green) or negative (red)
 * @param {string} props.trendValue - Trend percentage value (e.g., "4%")
 * @param {string} props.trendPeriod - Trend comparison period text (e.g., "vs. Last 30 Days")
 * @param {Array} props.metrics - Array of secondary metrics { label, value }
 * @param {number} props.totalVehicles - Total vehicles count for percentage calculation
 */
const KPICard = ({
    title,
    titleTooltip,
    value,
    unit,
    previousValue,
    trend,
    isPositive = true,
    trendValue,
    trendPeriod,
    metrics,
    totalVehicles,
}) => {
    let TrendIcon;
    let trendColor;
    let tooltipText;
    let trendHoverColor;

    const isNoChange = trend === "neutral" || trendValue === "0.0%" || trendValue === "0%";

    if (trend === "up") {
        TrendIcon = ArrowUp;
        trendColor = isPositive ? "#008A15" : "#B50500";
        trendHoverColor = isPositive ? '#04580F' : '#930500';
        tooltipText = `${previousValue}${unit ? ` ${unit}` : ''} ${trendPeriod}`;
    } else if (trend === "down") {
        TrendIcon = ArrowDown;
        trendColor = isPositive ? "#008A15" : "#B50500";
        trendHoverColor = isPositive ? '#04580F' : '#930500';
        tooltipText = `${previousValue}${unit ? ` ${unit}` : ''} ${trendPeriod}`;
    } else {
        TrendIcon = ArrowNoChange;
        trendColor = "#686868";
        trendHoverColor = '#494949';
        tooltipText = isNoChange ? `No change ${trendPeriod}` : `${previousValue}${unit ? ` ${unit}` : ''} ${trendPeriod}`;
    }

    return (
        <Box
            sx={{
                backgroundColor: "#FFFFFF",
                borderRadius: "12px",
                padding: "20px",
                border: "1px solid #E9E9E9",
                height: "156px",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
            }}
        >
            {/* Title */}
            <PrimaryTooltip
                title={titleTooltip}
                placement="bottom"
            >
                <Typography
                    variant="fw500s14lh24c2E2E2E"
                    sx={{
                        lineHeight: '20px',
                        color: "#494949",
                        "&:hover": {
                            textDecoration: 'underline'
                        }
                    }}
                >
                    {title}
                </Typography>
            </PrimaryTooltip>

            {/* Main Value with Unit */}
            <Box sx={{ display: "flex", flexDirection: 'column', gap: "8px" }}>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                    <Typography
                        variant="fw500s16lh24c000000"
                        display="inline"
                        sx={{
                            fontSize: "28px",
                            fontWeight: 700,
                        }}
                    >
                        {value}
                    </Typography>
                    {unit && (
                        <Typography
                            variant="fw400s14lh20c2E2E2E"
                            sx={{ color: "#686868" }}
                        >
                            {unit}
                        </Typography>
                    )}
                </Box>
                {trendValue && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                        }}
                    >
                        <PrimaryTooltip
                            title={tooltipText}
                            placement="bottom"
                        >
                            <Grid2 container alignItems="center" gap={0.25}>
                                <TrendIcon color={trendColor} sx={{ '&:hover': { color: trendHoverColor } }} />
                                <Typography
                                    variant="fw500s12c000"
                                    sx={{
                                        lineHeight: '24px',
                                        color: trendColor,
                                        cursor: 'default',
                                        '&:hover': { color: trendHoverColor }
                                    }}
                                >
                                    {trendValue}
                                </Typography>
                            </Grid2>
                        </PrimaryTooltip>
                        <Typography
                            variant="fw500s12c000"
                            sx={{ color: "#8F8F8F" }}
                        >
                            {trendPeriod}
                        </Typography>
                    </Box>
                )}
            </Box>

            <Divider sx={{ my: 0, borderColor: '#E6E6E6' }} />

            {/* Metrics */}
            {metrics && metrics.length > 0 && (
                <Grid2 container flexDirection={'column'} spacing={0.5}>
                    {metrics.map((metric, index) => {
                        const isVehiclesAffected = metric.label === "Vehicles Affected";
                        let percentage = null;
                        if (isVehiclesAffected && totalVehicles && totalVehicles > 0) {
                            const rawPercentage = (parseFloat(metric.value) / totalVehicles) * 100;
                            percentage = parseFloat(rawPercentage.toFixed(2));
                        }

                        const metricValue = (
                            <Grid2 container gap={0.5}>
                                <Typography
                                    variant="fw400s14lh20c2E2E2E"
                                    sx={{ color: "#000000" }}
                                >
                                    {metric.value}
                                </Typography>
                                {isVehiclesAffected && percentage !== null && (
                                    <Typography
                                        variant="fw400s14lh20c2E2E2E"
                                        sx={{ color: "#686868" }}
                                    >
                                        ({percentage}%)
                                    </Typography>
                                )}
                            </Grid2>
                        );

                        return (
                            <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                <Typography
                                    variant="fw400s14lh20c2E2E2E"
                                    sx={{ color: "#686868" }}
                                >
                                    {metric.label}
                                </Typography>
                                {isVehiclesAffected && percentage !== null ? (
                                    <PrimaryTooltip
                                        title={`${metric.value} vehicles affected (${percentage}% of total vehicles)`}
                                        placement="bottom"
                                    >
                                        <Box sx={{ cursor: 'default' }}>
                                            {metricValue}
                                        </Box>
                                    </PrimaryTooltip>
                                ) : (
                                    metricValue
                                )}
                            </Box>
                        );
                    })}
                </Grid2>
            )}
        </Box>
    );
};

export default KPICard;
